import React, { useState, useCallback, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch } from 'react-redux'
import { resetPassword, verifyEmail } from 'actions/authActions'
import Waiting from '../Waiting.jsx'
import moment from 'moment'

const ResetPassword = ({ queryParams, setStateMessageRef, message }) => {
  const dispatch = useDispatch()
  const [password, setPassword] = useState('')
  const verifying = queryParams.id != null && queryParams.hash != null
  const [waiting, setWaiting] = useState(verifying)
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [errors, setErrors] = useState(null)

  useEffect(() => {
    const verificationExpiredMessage =
      'This email verification link has expired. Please contact your system administrator to request a new link.'
    const resetExpiredMessage =
      'This password reset link has expired. Please use "Forgot password" to request a new link.'
    if (
      message !== verificationExpiredMessage &&
      message !== resetExpiredMessage
    ) {
      if (
        queryParams.expires &&
        setStateMessageRef.current &&
        moment.unix(queryParams.expires).isBefore(moment())
      ) {
        if (verifying) {
          setStateMessageRef.current('summit-login', verificationExpiredMessage)
        } else {
          setStateMessageRef.current('summit-login', resetExpiredMessage)
        }
      } else if (
        queryParams.reset_expires &&
        setStateMessageRef.current &&
        moment.unix(queryParams.reset_expires).isBefore(moment())
      ) {
        setStateMessageRef.current('summit-login', resetExpiredMessage)
      }
    }
  }, [
    setStateMessageRef.current,
    queryParams.expires,
    queryParams.reset_expires,
    message
  ])

  useEffect(() => {
    // did mount
    if (verifying) {
      // need to do email verify
      const errorCallback = response => {
        setStateMessageRef.current(
          'login',
          'Unable to verify email. Please contact your system administrator.'
        )
      }
      const successCallback = result => {
        if (!queryParams.id || !queryParams.token) {
          // this is an email verification or password reset
          if (result.status === 200 || result.status === 204) {
            setStateMessageRef.current('login', 'Email verified. Please login.')
          } else {
            setStateMessageRef.current(
              'login',
              'Unable to verify email. Please contact your system administrator.'
            )
          }
        } else {
          // this is a new account setup (email verification AND password reset)
          if (result.status === 204 || result.status === 200) {
            // nothing to do other then setWaiting false so they can enter their new pw
            setWaiting(false)
          } else {
            setStateMessageRef.current(
              'login',
              'Unable to verify email. Please contact your system administrator.'
            )
          }
        }
      }
      const callbacks = {
        error: errorCallback,
        200: successCallback,
        204: successCallback // already verified
      }
      dispatch(verifyEmail(callbacks))
    }
  }, [])

  const doPasswordResetSubmit = useCallback(
    e => {
      e.preventDefault()

      const callbacks = {
        400: response => {
          const { errors = {} } = response.data
          setErrors(errors)
          setWaiting(false)
        },
        403: response => {
          const { message = {} } = response.data
          const { message: errorMessage = 'Unknown error.' } = message
          setStateMessageRef.current(
            'summit-login',
            'Error: ' + errorMessage + ' Please resend email and try again.'
          )
        },
        422: response => {
          const { errors = {} } = response.data
          if (
            errors.email &&
            errors.email[0] &&
            (errors.email[0] === 'This password reset token is invalid.' ||
              errors.email[0] === 'This password reset token is expired.')
          ) {
            setErrors(null)
            setStateMessageRef.current(
              'summit-login',
              'Password recovery token expired. Please resend email and try again.'
            )
          } else {
            setErrors(errors)
          }
          setWaiting(false)
        },
        200: () => {
          setPassword('')
          setPasswordConfirmation('')
          setErrors(null)
          setStateMessageRef.current(
            'summit-login',
            'Password reset successful. Please login using your new password.'
          )
        },
        500: () => {
          setWaiting(false)
          setErrors(null)
          setStateMessageRef.current(
            'recover-password',
            'An unknown error occurred. Please try again.'
          )
        }
      }
      setWaiting(true)
      dispatch(resetPassword(password, passwordConfirmation, callbacks))
    },
    [password, passwordConfirmation]
  )

  if (waiting) {
    return <Waiting />
  } else {
    return (
      <>
        <h4>
          <div className="login-text" style={{ marginBottom: '20px' }}>
            {message || 'Enter your new password'}
          </div>
        </h4>
        {errors != null ? (
          <div className="alert alert-danger" role="alert" data-cy="alert">
            <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} size="1x" />
            Validation errors:
            <ul>
              {Object.keys(errors).map((property, idx1) => {
                return errors[property].map((error, idx2) => {
                  return (
                    <li key={`${idx1}-${idx2}`}>{`${property}: ${error}`}</li>
                  )
                })
              })}
            </ul>
          </div>
        ) : null}
        <form className="reset-inputs" onSubmit={doPasswordResetSubmit}>
          <input
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            aria-label="Password reset input"
            className="form-control input-lg"
            type="password"
            autoFocus
            data-cy="password"
          />
          <br />
          <input
            value={passwordConfirmation}
            onChange={e => setPasswordConfirmation(e.target.value)}
            placeholder="Re-enter password"
            aria-label="Password confirmation input"
            className="form-control input-lg"
            type="password"
            data-cy="password"
          />
          <input className="hidden" type="submit" />
        </form>
        <br />
        <button
          type="submit"
          aria-label="submit button"
          className="btn btn-warning btn-lg login-button"
          onClick={doPasswordResetSubmit}
        >
          Reset Password
        </button>
      </>
    )
  }
}

export default ResetPassword
